<template>
  <div class="header">
    <nav id="navbar" ref="navbar" class="navbar navbar-expand-lg navbar-light bg-light">
      <div class="container-sm">
        <!-- 左：LOGO + 語言（保留） -->
        <a class="navbar-brand p-0 d-flex flex-column align-items-start" href="/">
          <img class="navbar-logo" src="@/assets/logo.png" />
        </a>

        <!-- 漢堡 -->
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          :aria-expanded="isMenuOpen ? 'true' : 'false'"
          aria-label="Toggle navigation"
        >
          <span class="toggler-icon" :class="{ open: isMenuOpen }" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div class="collapse navbar-collapse" id="navbarSupportedContent">
          <!-- ✅ /app 模式：tab 隱藏 → 標題 + 登出 + 社群 -->
          <template v-if="isAppRoute">
            <!-- 中：標題（桌機顯示） -->
            <div class="app-title flex-grow-1 text-center d-none d-lg-block">
              <span class="app-title-text">{{ appTitle }}</span>
            </div>

            <!-- 右：登出 + 社群 -->
            <div class="d-flex align-items-center ms-auto app-right-group">
              <button type="button" class="btn btn-sm logout-btn me-2" @click="onSignOut">
                {{ $t("common.logout") || "Logout" }}
              </button>

              <div class="lang-dropdown ms-2 my-2 my-lg-0">
                <select class="form-select form-select-sm" v-model="$i18n.locale" aria-label="Language">
                  <option v-for="(langItem, i) in langs" :key="`AppLangOpt${i}`" :value="langItem.lang">
                    {{ langItem.name }}
                  </option>
                </select>
              </div>

              <form class="social-form ms-lg-3">
                <ul class="navbar-nav social-nav">
                  <li class="m-1 center">
                    <a href="https://www.facebook.com/TSKlabTaiwan" target="_blank" rel="noopener">
                      <img class="w-100 h-100" src="@/assets/images/facebook.png" />
                    </a>
                  </li>
                  <li class="m-1 center">
                    <a
                      href="https://www.instagram.com/tsk_taiwan/?igsh=eXZoZnF6aGIwaGF5"
                      target="_blank"
                      rel="noopener"
                    >
                      <img class="w-100 h-100" src="@/assets/images/ig.png" />
                    </a>
                  </li>
                  <li class="m-1 center">
                    <a href="https://lin.ee/3q7G3vO3" target="_blank" rel="noopener">
                      <img class="w-100 h-100" src="@/assets/images/line.jpg" />
                    </a>
                  </li>
                </ul>
              </form>
            </div>
          </template>

          <!-- 一般模式：維持你原本 tab + 社群 -->
          <template v-else>
            <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
              <li class="nav-item" v-for="(t, index) in tab" :key="index">
                <a
                  v-if="t.action === 'homeTop'"
                  :id="`tab-${index}`"
                  class="nav-link"
                  :class="{ active: isHome }"
                  href="#"
                  @click.prevent="goHomeTop(); closeMenu()"
                >
                  {{ $t(t.name) }}
                </a>

                <!-- ✅ 交給 router 自己處理 active（重點修正） -->
                <router-link
                  v-else-if="t.type === 'route'"
                  :id="`tab-${index}`"
                  class="nav-link"
                  :to="t.to"
                  exact-active-class="active"
                  @click="closeMenu"
                >
                  {{ $t(t.name) }}
                </router-link>

                <a
                  v-else
                  :id="`tab-${index}`"
                  class="nav-link"
                  :href="isHome ? t.to : `/${t.to}`"
                  @click="closeMenu"
                >
                  {{ $t(t.name) }}
                </a>
              </li>
            </ul>

            <!-- 語言下拉（放在 tab 與社群之間） -->
            <div class="lang-dropdown ms-lg-3 my-2 my-lg-0">
              <select class="form-select form-select-sm" v-model="$i18n.locale" aria-label="Language">
                <option v-for="(langItem, i) in langs" :key="`LangOpt${i}`" :value="langItem.lang">
                  {{ langItem.name }}
                </option>
              </select>
            </div>

            <form class="social-form ms-lg-3">
              <ul class="navbar-nav social-nav">
                <li class="m-1 center">
                  <a
                    href="https://www.facebook.com/TSKlabTaiwan"
                    target="_blank"
                    rel="noopener"
                    @click="closeMenu"
                  >
                    <img class="w-100 h-100" src="@/assets/images/facebook.png" />
                  </a>
                </li>

                <li class="m-1 center">
                  <a
                    href="https://www.instagram.com/tsk_taiwan/?igsh=eXZoZnF6aGIwaGF5"
                    target="_blank"
                    rel="noopener"
                    @click="closeMenu"
                  >
                    <img class="w-100 h-100" src="@/assets/images/ig.png" />
                  </a>
                </li>

                <li class="m-1 center">
                  <a href="https://lin.ee/3q7G3vO3" target="_blank" rel="noopener" @click="closeMenu">
                    <img class="w-100 h-100" src="@/assets/images/line.jpg" />
                  </a>
                </li>
              </ul>
            </form>
          </template>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { lang, tab } from "@/config/headerConfig.js";
import { supabase } from "@/lib/supabase";

export default {
  name: "Header",

  props: {
    appTitle: { type: String, default: "專屬學院" },
  },

  data() {
    return {
      langs: lang,
      tab: tab,
      onScrollHandler: null,
      isMenuOpen: false,
    };
  },

  computed: {
    isHome() {
      return this.$route && this.$route.path === "/";
    },
    isAppRoute() {
      const path = (this.$route && this.$route.path) || "/";
      return path === "/app" || path.startsWith("/app/");
    },
  },

  watch: {
    "$i18n.locale"() {
      this.closeMenu(); // ✅ 選完語言就收起選單
    },

    // ✅ 離開首頁時，把「首頁錨點用的 active」清掉，避免殘影卡住
    $route() {
      if (!this.isHome) {
        this.clearAnchorActives();
      } else {
        // 回到首頁時，讓 scroll 高亮馬上刷新一次
        this.$nextTick(() => this.onScrollHandler && this.onScrollHandler());
      }
    },
  },

  methods: {
    closeMenu() {
      const collapseEl = document.getElementById("navbarSupportedContent");
      if (!collapseEl) return;

      const isShown = collapseEl.classList.contains("show");
      if (!isShown) return;

      const toggler = document.querySelector('[data-bs-target="#navbarSupportedContent"]');
      toggler?.click();
    },

    goHomeTop() {
      if (this.isHome) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      this.$router.push("/");
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    },

    async onSignOut() {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.warn("signOut failed:", e?.message || e);
      } finally {
        this.$router.replace("/member");
      }
    },

    // =========================
    // ✅ 新增：只處理首頁錨點 tab 的 active
    // =========================
    clearAnchorActives() {
      (this.tab || []).forEach((t, idx) => {
        // 只清 anchor + homeTop，route 交給 router
        if (t.type === "anchor" || t.action === "homeTop") {
          document.getElementById(`tab-${idx}`)?.classList.remove("active");
        }
      });
    },

    setActiveByIndex(idx) {
      if (idx < 0) return;
      document.getElementById(`tab-${idx}`)?.classList.add("active");
    },

    setActiveByName(name) {
      const idx = (this.tab || []).findIndex((x) => x.name === name);
      this.setActiveByIndex(idx);
    },
  },

  mounted() {
    const navbar = document.getElementById("navbar");
    if (navbar) {
      document.documentElement.style.setProperty("--navbar-height", navbar.offsetHeight + "px");
    }

    const collapseEl = document.getElementById("navbarSupportedContent");
    if (collapseEl) {
      collapseEl.addEventListener("show.bs.collapse", () => {
        this.isMenuOpen = true;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : "";
      });

      collapseEl.addEventListener("hidden.bs.collapse", () => {
        this.isMenuOpen = false;

        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      });
    }

    this.onScrollHandler = () => {
      // 固定 Header
      const navbar = document.getElementById("navbar");
      if (!navbar) return;

      if (window.scrollY > navbar.offsetHeight) {
        navbar.classList.add("fixed-top");
        document.body.style.paddingTop = navbar.offsetHeight + "px";
      } else {
        navbar.classList.remove("fixed-top");
        document.body.style.paddingTop = "0";
      }

      // ✅ /app 模式不跑 tab 高亮
      if (this.isAppRoute) return;

      // ✅ 只有首頁才跑錨點高亮
      if (!this.isHome) return;
      if (document.getElementsByClassName("home").length <= 0) return;

      let active = false;

      (this.tab || [])
        .filter((e) => e.type === "anchor" && typeof e.to === "string" && e.to.startsWith("#"))
        .forEach((e) => {
          const id = e.to.replace("#", "");
          const el = document.getElementById(id);
          if (!el) return;

          const rect = el.getBoundingClientRect();
          const full = rect.top >= 0 && rect.bottom <= window.innerHeight;
          const part = rect.top < window.innerHeight / 2 && rect.bottom >= 0;

          if (full || part) {
            // ✅ 只清 anchor/homeTop 的 active，不碰 route tab
            this.clearAnchorActives();
            this.setActiveByName(e.name);
            active = true;
          }
        });

      if (!active) {
        // ✅ 沒命中任何 section：預設回 homeTop（只管 anchor/homeTop）
        this.clearAnchorActives();
        const aboutIndex = (this.tab || []).findIndex((x) => x.action === "homeTop");
        this.setActiveByIndex(aboutIndex);
      }
    };

    window.addEventListener("scroll", this.onScrollHandler);

    // ✅ 初次載入也跑一次（避免一開始就沒高亮）
    this.$nextTick(() => this.onScrollHandler && this.onScrollHandler());
  },

  beforeUnmount() {
    if (this.onScrollHandler) window.removeEventListener("scroll", this.onScrollHandler);
  },
};
</script>

<style lang="scss">
.navbar-logo {
  width: 157px;
  height: 36px;
  object-fit: contain;
  display: block;
}

/* 自訂漢堡 / X icon */
.navbar-toggler {
  border: 0;
  box-shadow: none !important;
}

.toggler-icon {
  width: 24px;
  height: 18px;
  position: relative;
  display: inline-block;
}

.toggler-icon span {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background: currentColor;
  border-radius: 2px;
  transition: transform 0.2s ease, top 0.2s ease, opacity 0.2s ease;
}

.toggler-icon span:nth-child(1) { top: 0; }
.toggler-icon span:nth-child(2) { top: 8px; }
.toggler-icon span:nth-child(3) { top: 16px; }

.toggler-icon.open span:nth-child(1) { top: 8px; transform: rotate(45deg); }
.toggler-icon.open span:nth-child(2) { opacity: 0; }
.toggler-icon.open span:nth-child(3) { top: 8px; transform: rotate(-45deg); }

.lang-dropdown {
  min-width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lang-dropdown .form-select {
  height: 26px;
  padding: 0 22px 0 10px;
  font-size: 0.8rem;
  line-height: 26px;

  width: auto;
  min-width: 88px;
  max-width: 120px;

  border-radius: 5px;
  border: 1px solid #e5e7eb;
  background-color: #f3f4f6;
  color: #374151;

  box-shadow: none;
  cursor: pointer;

  background-image: none !important;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    border-color: #9ca3af;
    box-shadow: none;
  }
}

.navbar-nav {
  .active,
  .router-link-exact-active {
    color: var(--bs-primary) !important;
    font-weight: bold;
  }
}

.social-nav {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 30px;
  margin: 0;
  padding: 0;
}

.social-nav li {
  width: 36px;
  height: 36px;
  list-style: none;
}

.social-form {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-left: 0 !important;
}

@media (min-width: 992px) {
  .social-form {
    width: auto;
  }
}

.app-title-text {
  letter-spacing: 0.25em;
  font-weight: 700;
  color: var(--bs-primary);
  white-space: nowrap;
}

.logout-btn {
  border-radius: 0;
  border: 0;
  background: transparent;
  color: #6b7280 !important;
  font-weight: 500;
  padding: 0 8px;
  font-size: 0.9rem;
  white-space: nowrap;
  box-shadow: none;
}
.logout-btn:hover {
  background: transparent;
  color: #374151 !important;
  text-decoration: underline;
}

.app-right-group {
  display: flex;
  align-items: center;
}

/* 手機版：漢堡選單全螢幕覆蓋 */
@media (max-width: 991.98px) {
  .navbar-collapse {
    position: fixed;
    top: var(--navbar-height, 56px);
    left: 0;
    right: 0;
    bottom: 0;

    background-color: #fff;
    padding: 1.5rem 1rem;

    overflow-y: auto;
    z-index: 1040;

    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
    transition: transform 0.22s ease, opacity 0.22s ease;
    will-change: transform, opacity;
  }

  .navbar-collapse.show {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  .navbar-collapse.collapsing {
    height: auto !important;
    transition: none !important;
  }
}

/* 手機版 /app：展開選單時改直向排列 */
@media (max-width: 991.98px) {
  .app-right-group {
    width: 100%;
    margin-left: 0 !important;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
  }

  .app-right-group .logout-btn {
    margin-right: 0 !important;
  }

  .app-right-group .lang-dropdown {
    margin-left: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .app-right-group .social-form {
    width: 100%;
    justify-content: center;
    margin-left: 0 !important;
  }

  .app-right-group .social-nav {
    justify-content: center;
  }
}

/* 手機版：文字放大 + 行距拉大 */
@media (max-width: 991.98px) {
  .navbar-collapse .nav-link {
    font-size: 1.25rem;
    line-height: 1.6;
    padding: 14px 0;
  }

  .navbar-collapse .nav-item + .nav-item {
    margin-top: 6px;
  }

  .navbar-collapse .logout-btn {
    font-size: 1.1rem;
    padding: 10px 14px;
  }

  .navbar-collapse .lang-dropdown .form-select {
    height: 38px;
    font-size: 1rem;
    line-height: 38px;
    padding: 0 34px 0 12px;
    border-radius: 8px;
  }

  .navbar-collapse .app-right-group {
    gap: 18px;
  }

  .navbar-collapse .social-nav {
    gap: 14px;
    margin-top: 8px;
  }
}

/* 手機版：語言下拉選單無箭頭＋文字置中 */
@media (max-width: 991.98px) {
  .navbar-collapse .lang-dropdown .form-select {
    background-image: none !important;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    text-align: center;
    text-align-last: center;
    padding-right: 12px;
  }
}

/* 手機版：社群 icon 整行往下移 */
@media (max-width: 991.98px) {
  .navbar-collapse .social-form {
    margin-top: 24px;
  }

  .navbar-collapse .social-nav {
    margin-top: 4px;
  }
}
</style>
